import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export default function DemoPostcardBuilder() {
  const { t } = useTranslation();
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);

  // Load session token
  useEffect(() => {
    const token = localStorage.getItem('demoSessionToken');
    setSessionToken(token);

    if (token) {
      fetchMessages(token);
    }
  }, []);

  const fetchMessages = async (token) => {
    try {
      const res = await fetch(`/api/demo/messages/${token}`);
      const data = await res.json();
      
      if (data.status === 'success') {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  };

  const handleAddMessage = async () => {
    if (!messageText.trim()) {
      toast.error('Nachricht ist leer');
      return;
    }

    if (!sessionToken) {
      toast.error('Session ungültig');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/demo/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken,
          text: messageText,
          author: 'Demo User',
        }),
      });

      if (!res.ok) throw new Error('Failed to add message');

      const data = await res.json();
      setMessages([...messages, data.message]);
      setMessageText('');
      toast.success('Nachricht hinzugefügt! 📮');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Nachricht konnte nicht hinzugefügt werden');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMessages([]);
    setMessageText('');
    toast.success('Zurückgesetzt');
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-lg p-6 mb-6"
      >
        <h2 className="text-2xl font-bold mb-2">📮 Postcard Demo</h2>
        <p>Erstelle eine Postkarte und teile Nachrichten mit deinen Freunden!</p>
      </motion.div>

      {/* Message List */}
      <div className="mb-6">
        <h3 className="font-bold text-lg mb-3">Deine Nachrichten ({messages.length})</h3>
        {messages.length === 0 ? (
          <p className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
            Noch keine Nachrichten - Schreib deine erste! ✍️
          </p>
        ) : (
          <div className="space-y-3">
            {messages.map((msg, idx) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white border-l-4 border-orange-400 p-4 rounded-lg shadow-sm"
              >
                <p className="text-gray-700">{msg.text}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {new Date(msg.createdAt).toLocaleTimeString('de-DE')}
                </p>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Input Section */}
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <textarea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder="Schreib deine Nachricht für die Postkarte..."
          maxLength={500}
          rows={4}
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
        />

        <div className="mt-3 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {messageText.length}/500 Zeichen
          </span>

          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition flex items-center gap-2"
            >
              <RotateCcw size={18} /> Zurücksetzen
            </button>

            <button
              onClick={handleAddMessage}
              disabled={loading || !messageText.trim()}
              className="px-6 py-2 bg-orange-400 text-white rounded-lg hover:bg-orange-500 disabled:opacity-50 transition flex items-center gap-2"
            >
              <Send size={18} /> {loading ? 'Speichern...' : 'Nachricht speichern'}
            </button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6 text-sm text-blue-900">
        <p>
          <strong>💡 Tipp:</strong> Bei Postcard-Paketen kannst du nur Nachrichten speichern.
          Keine Events oder Empfänger möglich. Upgrade für mehr Features!
        </p>
      </div>
    </div>
  );
}
